import React, { useEffect, useState } from "react";
import { Typography, TextField, Button, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Loader from "react-loader";

// components
import MainLayout from "../components/hoc/MainLayout";
import LocationAutocomplete from "../components/LocationAutocomplete";

// helpers
import validateEmail from "../helpers/validateEmail";
import { apiGet, apiPut } from "../helpers/api";
import { useSnackbar } from "notistack";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: "#A3080C",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#bd0d12",
      borderColor: "#564345",
    },
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

function EditDelivery(props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const valuesObject = {
    orderId: "",
    contactInfo: "",
    contactNumber: "",
    contactEmail: "",
    landmark: "",
    location: "",
    map: "",
  };

  const [delivery, setDelivery] = useState(null);
  const [values, setValues] = useState(valuesObject);
  const [errors, setErrors] = useState(valuesObject);
  const [loaded, setLoaded] = useState(true);

  const fetchDelivery = async () => {
    try {
      setLoaded(false);
      const response = await apiGet(`/deliveries/${props.match.params.uuid}`);
      const deliveryDetails = response.data.data;

      setLoaded(true);

      //   set delivery state
      setDelivery(deliveryDetails);

      //   set values state
      setValues({
        orderId: deliveryDetails.orderId,
        contactInfo: deliveryDetails.name,
        contactNumber: deliveryDetails.mobileNumber,
        contactEmail: deliveryDetails.emailAddress,
        landmark: deliveryDetails.landmark,
        location: deliveryDetails.address,
        map: `${deliveryDetails.customerPosition.latitude},${deliveryDetails.customerPosition.longitude}`,
      });

      return response.data.data;
    } catch (err) {
      console.log(err.response?.data?.message);
    }
  };

  useEffect(() => {
    if (!delivery) {
      fetchDelivery();
    }
  });

  const validateForm = () => {
    // clear errors
    setErrors(valuesObject);

    let hasErrors = false;

    let keys = Object.keys(values);

    if (delivery && delivery.isPickup) {
      keys = keys.filter(
        (key) => !["landmark", "location", "map"].includes(key)
      );
    }

    // validate form
    keys.forEach((key) => {
      // make sure everything is filled out
      if (values[key]?.trim() === "") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [key]: "This field is required.",
        }));
        hasErrors = true;
      }

      // validate email
      if (key === "contactEmail" && !validateEmail(values.contactEmail)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          contactEmail: "Invalid email.",
        }));
        hasErrors = true;
      }
    });

    return !hasErrors;
  };

  const formHandler = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoaded(false);

      // get latitude and longitude data
      const mapArray = values.map.split(",");

      try {
        // send data to api endpoint
        const response = await apiPut(`/deliveries/${delivery.id}`, {
          order_id: values.orderId,
          name: values.contactInfo,
          email_address: values.contactEmail,
          mobile_number: values.contactNumber,
          landmark: values.landmark,
          address: values.location,
          latitude: mapArray[0],
          longitude: mapArray[1],
        });

        setLoaded(true);
        if (response.data.data) {
          props.history.push(`/delivery/${response.data.data.id}`);
        }
      } catch (err) {
        console.log(err.response?.data?.message);
        setLoaded(true);
      }
    }
  };

  const renderTextField = (id, label, fieldName, fieldType = "text") => {
    return (
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id={id}
        label={label}
        autoFocus={fieldName === "orderId"}
        type={fieldType}
        value={values[fieldName]}
        error={!!errors[fieldName]}
        helperText={errors[fieldName]}
        onChange={(e) =>
          setValues((prevValues) => ({
            ...prevValues,
            [fieldName]: e.target.value,
          }))
        }
      />
    );
  };

  let center = null;
  if (values.map) {
    const mapArray = values.map.split(",");
    center = {
      lat: parseFloat(mapArray[0]),
      lng: parseFloat(mapArray[1]),
    };
  }

  const handlePickupSetReadyForPickup = async () => {
    try {
      await apiPut(`/deliveries/pickup/${delivery.id}/ready`, {});
      enqueueSnackbar(
        "Success, we will send an sms to the customer that the order is ready for pickup",
        {
          autoHideDuration: 3000,
        }
      );
	  props.history.push('/');
    //   await fetchDelivery();
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e.response?.data?.message ?? "Failed to set ready", {
        autoHideDuration: 3000,
      });
    }
  };

  const handlePickupSetComplete = async () => {
    try {
      await apiPut(`/deliveries/pickup/${delivery.id}/complete`, {});
      enqueueSnackbar(
        "Successfully set pickup to complete",
        {
          autoHideDuration: 3000,
        }
      );
	  props.history.push('/');
    //   await fetchDelivery();
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e.response?.data?.message ?? "Failed to set ready", {
        autoHideDuration: 3000,
      });
    }
  };

  console.log(delivery);

  return (
    <Loader loaded={!!loaded}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5" gutterBottom>
          Edit {delivery && delivery.isPickup ? "Pickup" : "Delivery"}
        </Typography>

        {delivery && delivery.isPickup ? (
          <div>
            {delivery.pickupStatus === 0 && (
              <Button
                disableElevation
				style={{
					backgroundColor: "#A3080C",
					padding: "16px",
					fontSize: 20,
					fontWeight: "bold",
					color: "#fff",
					"&:hover": {
					  backgroundColor: "#bd0d12",
					  borderColor: "#564345",
					},
				}}
                variant="contained"
                onClick={() => handlePickupSetReadyForPickup()}
              >
                Set Ready for Pickup
              </Button>
            )}

            {delivery.pickupStatus === 5 && (
              <Button
                disableElevation
				style={{
					backgroundColor: "#A3080C",
					padding: "16px",
					fontSize: 20,
					fontWeight: "bold",
					color: "#fff",
					"&:hover": {
					  backgroundColor: "#bd0d12",
					  borderColor: "#564345",
					},
				}}
                variant="contained"
                onClick={() => handlePickupSetComplete()}
              >
                Set to Complete
              </Button>
            )}
          </div>
        ) : null}
      </div>
      <form className={classes.form} onSubmit={formHandler}>
        {renderTextField("orderId", "Order ID", "orderId")}
        {renderTextField("contactInfo", "Contact Person", "contactInfo")}
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {renderTextField(
              "contactNumber",
              "Contact Number",
              "contactNumber"
            )}
          </Grid>
          <Grid item xs={6}>
            {renderTextField(
              "contactEmail",
              "Contact Email Address",
              "contactEmail",
              "email"
            )}
          </Grid>
        </Grid>

        {delivery &&
          delivery.type === "delivery" &&
          renderTextField("landmark", "Landmark", "landmark")}

        {delivery && delivery.type === "delivery" && (
          <LocationAutocomplete
            value={values.location}
            error={errors.location}
            center={center}
            onChange={(obj) => {
              setValues((prevValues) => ({
                ...prevValues,
                ...obj,
              }));
            }}
          />
        )}

        <Button
          type="submit"
          disableElevation
          variant="contained"
          fullWidth
          className={classes.submit}
        >
          Update Order
        </Button>
      </form>
    </Loader>
  );
}

export default MainLayout(EditDelivery, true);
